import React from 'react'
import className from 'classnames'
import {useRouter} from 'next/router'

import {t, validateEmail} from '@lib/helpers'
import {useAsync} from '@lib/hooks'
import {sendEmail} from '@lib/services'
import {acceptConsent} from '@lib/utils/consent'
import {useUTMContext, logEvent} from '@lib/gtm'

import Checkbox from './Checkbox'
import CloudflareTurnstileWidget from './CloudflareTurnstileWidget/CloudflareTurnstileWidget'

const ContactForm = ({alt}) => {
  const [accept, setAccept] = React.useState(false)
  const [turnstileToken, setTurnstileToken] = React.useState(null)

  const {run: runSendEmail, isLoading, isSuccess} = useAsync(sendEmail)
  const ref = React.useRef()

  const {data: utm} = useUTMContext()
  const router = useRouter()

  React.useEffect(() => {
    if (isSuccess && ref.current) {
      ref.current.reset()
      setAccept(false)
      setTurnstileToken(null)

      logEvent({
        ga: {
          category: 'Submit Form Success',
          action: 'Submit Form Success',
          label: router.pathname.replace('/', '').replaceAll('-', ' '),
        },
        fb: {
          event: 'Lead',
        },
      })
    }
  }, [isSuccess])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const {email, message, phone, topic, checkbox} = e.target.elements

    const emailData = {
      to: topic.value === 'jobs' ? 'hr@datawow.io' : process.env.SALES_EMAIL,
      subject: `[${window.location.host}] ${email.value} wants to contact with you`,
      message: `
        Email: ${email.value}
        Tel: ${phone.value}
        Message: ${message.value}
      `,
      'cf-turnstile-response': turnstileToken,
    }

    const reqBody = {
      email: email.value,
      message: message.value,
      template: topic.value === 'jobs' ? 'apply_job' : 'products_and_services',
      phone: phone.value,
      url: window.location.href,
      marketing_consent: checkbox.checked,
    }

    const res = await runSendEmail({
      emailData,
      reqBody,
      utm,
    })

    if (res.status === 200 && checkbox.checked) {
      await acceptConsent({
        apiKey: process.env.CONSENT_WOW_CONTACT_API_KEY,
        purposeId: process.env.CONSENT_WOW_MARKETING_CONSENT_PURPOSE_ID,
        data: {
          email: email.value,
          phone_number: phone.value,
        },
      })
    }
  }

  return (
    <React.Fragment>
      <form
        className='GetStarted__form'
        id='contact-form'
        ref={ref}
        onSubmit={handleSubmit}
      >
        <select name='topic' className='Input Input--select'>
          <option value='services'>Products and services</option>
          <option value='jobs'>Apply job</option>
        </select>

        <input
          type='email'
          className='Input'
          placeholder={t('layout.GetStarted.form.email')}
          name='email'
          onBlur={validateEmail}
          required
        />

        <input
          type='tel'
          className='Input'
          placeholder={t('layout.GetStarted.form.tel')}
          name='phone'
          pattern='\d{10}'
          title='Please enter a 10-digit phone number'
          required
        />

        <textarea
          className='Input'
          placeholder={t('layout.GetStarted.form.message')}
          rows={5}
          name='message'
        />

        <Checkbox
          className='GetStarted__form__checkbox'
          name='checkbox'
          isChecked={accept}
          onClick={() => {
            setAccept((prev) => !prev)
          }}
        >
          {t('layout.GetStarted.form.marketing_consent')} &#8203;
          <span style={{whiteSpace: 'nowrap'}}>
            {t('layout.GetStarted.form.unsubscribe')}
          </span>
        </Checkbox>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
            marginTop: '10px',
          }}
        >
          <CloudflareTurnstileWidget
            onVerify={(token) => setTurnstileToken(token)}
          />
        </div>

        <div className='GetStarted__form__buttons'>
          <button
            className={className('Button', {
              'Button--gray': alt,
            })}
            disabled={!turnstileToken || isLoading}
          >
            {isSuccess
              ? t('layout.GetStarted.form.sent')
              : isLoading
              ? t('layout.loading')
              : !turnstileToken
              ? t('layout.preparing')
              : t('layout.GetStarted.form.submit')}
          </button>

          <h2 className='GetStarted__form__text'>
            {t('layout.GetStarted.form.privacy_policy')}
          </h2>
        </div>
      </form>
    </React.Fragment>
  )
}

ContactForm.propTypes = {}

export default ContactForm
